import type { ReactNode } from 'react';
import type {
  TransactionEvent,
  TransactionStatus,
} from '../../domain/entities/sales.entity';
import { EventActor } from '@/shared/components/event-actor';
import {
  TransactionStatusBadge,
  TransactionStatusIcon,
  formatDateTime,
} from './display';
import { eventLabel } from './event-labels';
import { WebhookOriginButton } from '../components/WebhookOriginModal';

function str(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/** Pill neutra: fora da transação os valores variam por entidade e não há badge próprio. */
function ValuePill({ children }: { children: string }) {
  return (
    <span className="border-(--color-border) bg-(--color-bg-primary) text-(--color-text-primary) rounded-md border px-2 py-0.5 font-mono text-xs">
      {children}
    </span>
  );
}

/**
 * Monta o JSON completo do evento (metadados + payload) para inspeção. Quem usa
 * esta tela lê JSON — exibimos cru, formatado, atrás de um painel expansível.
 */
function eventJson(event: TransactionEvent): string {
  return JSON.stringify(
    {
      id: event.id,
      eventType: event.eventType,
      actorPlatformProfileId: event.actorPlatformProfileId,
      correlationId: event.correlationId ?? null,
      reason: event.reason,
      createdAt: event.createdAt,
      payload: event.payload,
    },
    null,
    2,
  );
}

/**
 * Linha da timeline de auditoria de qualquer entidade do Sales. Só evento de
 * transação tem status de transação no payload; os outros mostram o par
 * `oldValue → newValue` cru.
 *
 * `showWebhookOrigin` fica desligado dentro de modal: o de webhook abriria por
 * cima e o `Esc` fecha os dois juntos.
 */
export function EventRow({
  event,
  isLast = false,
  badge,
  showWebhookOrigin = false,
}: {
  event: TransactionEvent;
  isLast?: boolean;
  badge?: ReactNode;
  showWebhookOrigin?: boolean;
}) {
  const isTransaction = event.eventType.startsWith('TRANSACTION_');
  const oldValue = str(event.payload.oldValue);
  const newValue = str(event.payload.newValue ?? event.payload.status);
  const dotStatus = isTransaction
    ? ((newValue ?? oldValue) as TransactionStatus | null)
    : null;

  return (
    <li className="relative flex gap-4">
      {!isLast && (
        <span
          aria-hidden="true"
          className="bg-(--color-border) left-3.75 absolute -bottom-6 top-8 w-px"
        />
      )}
      <div className="border-(--color-border) bg-(--color-bg-primary) relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border">
        {dotStatus ? (
          <TransactionStatusIcon status={dotStatus} />
        ) : (
          <span className="bg-(--color-text-secondary) h-2 w-2 rounded-full" />
        )}
      </div>

      <div className="min-w-0 flex-1 pb-6">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <span className="text-(--color-text-primary) flex flex-wrap items-center gap-2 text-sm font-medium">
            {badge}
            {eventLabel(event.eventType)}
          </span>
          <span className="text-(--color-text-secondary) text-xs">
            {formatDateTime(event.createdAt)}
          </span>
        </div>

        <div className="mt-1.5 flex flex-wrap items-center gap-2">
          {oldValue && (
            <>
              {isTransaction ? (
                <TransactionStatusBadge
                  status={oldValue as TransactionStatus}
                />
              ) : (
                <ValuePill>{oldValue}</ValuePill>
              )}
              <span className="text-(--color-text-secondary) text-xs">→</span>
            </>
          )}
          {newValue &&
            (isTransaction ? (
              <TransactionStatusBadge status={newValue as TransactionStatus} />
            ) : (
              <ValuePill>{newValue}</ValuePill>
            ))}
          <span className="text-(--color-text-secondary) text-xs">
            <EventActor event={event} />
          </span>
        </div>

        {event.reason && (
          <p className="text-(--color-text-secondary) mt-1.5 whitespace-pre-wrap break-words text-xs">
            {event.reason}
          </p>
        )}

        <div className="mt-2 flex flex-wrap items-start gap-x-4 gap-y-2">
          <details className="group min-w-0 max-w-full">
            <summary className="text-(--color-text-secondary) hover:text-(--color-accent) focus-visible:ring-(--color-accent) flex cursor-pointer list-none items-center gap-1 rounded text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2">
              <svg
                className="h-3 w-3 shrink-0 transition-transform group-open:rotate-90"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m8.25 4.5 7.5 7.5-7.5 7.5"
                />
              </svg>
              Ver payload (JSON)
            </summary>
            <pre className="border-(--color-border) bg-(--color-bg-secondary) text-(--color-text-secondary) mt-2 max-h-72 overflow-auto rounded-lg border p-3 text-xs leading-relaxed">
              {eventJson(event)}
            </pre>
          </details>
          {showWebhookOrigin && event.correlationId && (
            <WebhookOriginButton inboxId={event.correlationId} />
          )}
        </div>
      </div>
    </li>
  );
}
